import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Grid
} from "@mui/material";
import { QrCode, Download, Email } from "@mui/icons-material";
import axios from "../../utils/axios";
import CommonPageLayout from "../../components/commonPageLayout/commonPageLayout";
import CustomButton from "../../components/CustomButton/Button";
import { getUserEmail } from "../../utils/commonFunctions";

const GenerateUserQrcodesPage: React.FC = () => {
  const [qrCodeType, setQrCodeType] = useState<string>("");
  const [quantity, setQuantity] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const handleGenerateQrCodes = async () => {
    if (!qrCodeType || !quantity) {
      setMessage({
        type: "error",
        text: "Please select QR code type and enter quantity"
      });
      return;
    }

    const quantityNum = parseInt(quantity);
    if (isNaN(quantityNum) || quantityNum <= 0) {
      setMessage({
        type: "error",
        text: "Please enter a valid quantity (positive number)"
      });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      // API configuration matching the provided specification
      const config: any = {
        method: "post",
        url: `${apiBaseUrl}/users/userqrcodes/generate/codetype/${qrCodeType}/${quantityNum}`,
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${localStorage.getItem("accessToken")}`
        },
        data: {} // Empty data as per the API specification
      };

      const response = await axios(config);

      if (response.status === 200) {
        // Get user email for the success message
        const userEmail = await getUserEmail();
        console.log("User email retrieved:", userEmail);

        setMessage({
          type: "success",
          text: `QR codes generated successfully! Report will be emailed to ${userEmail} shortly.`
        });
        // Reset form
        setQrCodeType("");
        setQuantity("");
      }
    } catch (error: any) {
      console.error("API Error:", error);
      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          error.message ||
          "Failed to generate QR codes. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Only allow numbers
    if (value === "" || /^\d+$/.test(value)) {
      setQuantity(value);
    }
  };

  return (
    <CommonPageLayout
      heading="Generate User QR Codes"
      subHeading="Create QR codes for users with specified type and quantity"
    >
      <Paper
        sx={{
          p: 3
        }}
      >
        {message && (
          <div style={{ marginBottom: "20px" }}>
            <Alert severity={message.type as any}>{message.text}</Alert>
          </div>
        )}
        <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
          QR Code Generation Settings
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>QR Code Type</InputLabel>
              <Select
                value={qrCodeType}
                onChange={(e) => setQrCodeType(e.target.value)}
                label="QR Code Type"
              >
                <MenuItem value="Key">Key</MenuItem>
                <MenuItem value="Card">Card</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Quantity"
              value={quantity}
              onChange={handleQuantityChange}
              variant="outlined"
              placeholder="Enter number of QR codes"
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            />
          </Grid>
        </Grid>

        <Box display="flex" mt={3}>
          <CustomButton
            callBack={handleGenerateQrCodes}
            disabled={loading || !qrCodeType || !quantity}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Generate QR Codes"
            )}
          </CustomButton>
        </Box>
      </Paper>
    </CommonPageLayout>
  );
};

export default GenerateUserQrcodesPage;
